// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Analytics.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Página de Analytics e Relatórios Bancários
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  SwapHoriz as SwapHorizIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  VerifiedUser as VerifiedUserIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import bankingService, { BankingAnalytics } from '../../services/banking.service';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Analytics: React.FC = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<string>('month');
  const [analytics, setAnalytics] = useState<BankingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bankingService.getAnalytics(period);
      setAnalytics(data);
    } catch (err) {
      setError(t('banking.analytics.errorLoading'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-CV').format(value);
  };

  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const handleExport = async (type: 'transfers' | 'bills' | 'credit' | 'kyc') => {
    try {
      const blob = await bankingService.exportData(type, { period });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `banking-${type}-${period}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar:', err);
    }
  };

  if (loading && !analytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Estrutura de defaults — garante que campos nested nunca são undefined
  const defaultAnalytics: BankingAnalytics = {
    period,
    transfers: {
      total: 0, count: 0, growth: 0,
      byType: {
        p2p: { amount: 0, count: 0 },
        bank: { amount: 0, count: 0 },
        international: { amount: 0, count: 0 },
      },
      byStatus: { completed: 0, pending: 0, failed: 0 },
    },
    billPayments: { total: 0, count: 0, byCategory: [], growth: 0 },
    credit: {
      totalDisbursed: 0, activeContracts: 0, defaultRate: 0, growth: 0,
      applications: { pending: 0, approved: 0, rejected: 0 },
    },
    kyc: {
      total: 0, pending: 0, approvalRate: 0,
      byLevel: { basic: 0, standard: 0, premium: 0, business: 0 },
    },
    timeline: [],
  };

  // Deep merge: API pode retornar estrutura parcial — sempre completar com defaults
  const a = analytics as any;
  const mockAnalytics: BankingAnalytics = {
    ...defaultAnalytics,
    ...(a || {}),
    transfers: {
      ...defaultAnalytics.transfers,
      ...(a?.transfers || {}),
      byType: { ...defaultAnalytics.transfers.byType, ...(a?.transfers?.byType || {}) },
      byStatus: { ...defaultAnalytics.transfers.byStatus, ...(a?.transfers?.byStatus || {}) },
    },
    billPayments: { ...defaultAnalytics.billPayments, ...(a?.billPayments || {}) },
    credit: {
      ...defaultAnalytics.credit,
      ...(a?.credit || {}),
      applications: { ...defaultAnalytics.credit.applications, ...(a?.credit?.applications || {}) },
    },
    kyc: {
      ...defaultAnalytics.kyc,
      ...(a?.kyc || {}),
      byLevel: { ...defaultAnalytics.kyc.byLevel, ...(a?.kyc?.byLevel || {}) },
    },
    timeline: a?.timeline ?? defaultAnalytics.timeline,
  };

  const transferTypeData = [
    { name: 'P2P', value: mockAnalytics.transfers?.byType?.p2p?.amount ?? 0 },
    { name: 'Bancária', value: mockAnalytics.transfers?.byType?.bank?.amount ?? 0 },
    { name: 'Internacional', value: mockAnalytics.transfers?.byType?.international?.amount ?? 0 },
  ];

  const kycLevelData = [
    { name: 'Básico', value: mockAnalytics.kyc?.byLevel?.basic ?? 0 },
    { name: 'Standard', value: mockAnalytics.kyc?.byLevel?.standard ?? 0 },
    { name: 'Premium', value: mockAnalytics.kyc?.byLevel?.premium ?? 0 },
    { name: 'Business', value: mockAnalytics.kyc?.byLevel?.business ?? 0 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {t('banking.analytics.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('banking.analytics.period')}</InputLabel>
            <Select
              value={period}
              label="Período"
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="week">{t('banking.analytics.lastWeek')}</MenuItem>
              <MenuItem value="month">{t('banking.analytics.lastMonth')}</MenuItem>
              <MenuItem value="quarter">{t('banking.analytics.lastQuarter')}</MenuItem>
              <MenuItem value="year">{t('banking.analytics.lastYear')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAnalytics}
            disabled={loading}
          >
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* KPIs Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Transferências
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatCurrency(mockAnalytics.transfers.total)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(mockAnalytics.transfers.count)} transações
                  </Typography>
                </Box>
                <Box sx={{
                  bgcolor: 'primary.light',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SwapHorizIcon color="primary" />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                {mockAnalytics.transfers.growth >= 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography
                  variant="body2"
                  color={mockAnalytics.transfers.growth >= 0 ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5 }}
                >
                  {mockAnalytics.transfers.growth > 0 ? '+' : ''}{mockAnalytics.transfers.growth}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  vs período anterior
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Pagamento de Faturas
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatCurrency(mockAnalytics.billPayments.total)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(mockAnalytics.billPayments.count)} pagamentos
                  </Typography>
                </Box>
                <Box sx={{
                  bgcolor: 'success.light',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ReceiptIcon color="success" />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                {mockAnalytics.billPayments.growth >= 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography
                  variant="body2"
                  color={mockAnalytics.billPayments.growth >= 0 ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5 }}
                >
                  {mockAnalytics.billPayments.growth > 0 ? '+' : ''}{mockAnalytics.billPayments.growth}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  vs período anterior
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Crédito Desembolsado
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatCurrency(mockAnalytics.credit.totalDisbursed)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(mockAnalytics.credit.activeContracts)} contratos ativos
                  </Typography>
                </Box>
                <Box sx={{
                  bgcolor: 'warning.light',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CreditCardIcon color="warning" />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                {mockAnalytics.credit.growth >= 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography
                  variant="body2"
                  color={mockAnalytics.credit.growth >= 0 ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5 }}
                >
                  {mockAnalytics.credit.growth > 0 ? '+' : ''}{mockAnalytics.credit.growth}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  vs período anterior
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Verificações KYC
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatNumber(mockAnalytics.kyc.total)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mockAnalytics.kyc.pending} pendentes
                  </Typography>
                </Box>
                <Box sx={{
                  bgcolor: 'info.light',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <VerifiedUserIcon color="info" />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="success.main">
                  {mockAnalytics.kyc.approvalRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  taxa de aprovação
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Volume ao Longo do Tempo
                </Typography>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('transfers')}>
                  Exportar
                </Button>
              </Box>
              <ResponsiveContainer width="100%" height={300} debounce={50}>
                <AreaChart data={mockAnalytics.timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('pt-CV', { timeZone: 'UTC', day: '2-digit', month: 'short' })}
                  />
                  <YAxis tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value as number)}
                    labelFormatter={(label) => new Date(label as string).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="transfers" name="Transferências" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="bills" name="Faturas" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="credit" name="Crédito" stroke="#ffc658" fill="#ffc658" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Transferências por Tipo
              </Typography>
              <ResponsiveContainer width="100%" height={250} debounce={50}>
                <PieChart>
                  <Pie
                    data={transferTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {transferTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value as number)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Pagamentos por Categoria
                </Typography>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('bills')}>
                  Exportar
                </Button>
              </Box>
              <ResponsiveContainer width="100%" height={250} debounce={50}>
                <BarChart data={mockAnalytics.billPayments.byCategory} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                  <YAxis
                    dataKey="category"
                    type="category"
                    width={80}
                    tickFormatter={(value) => {
                      const labels: Record<string, string> = {
                        electricity: 'Eletricidade',
                        water: 'Água',
                        telecom: 'Telecom',
                        internet: 'Internet',
                        tv: 'TV',
                        insurance: 'Seguros',
                        government: 'Governo',
                        education: 'Educação',
                      };
                      return labels[value] || value;
                    }}
                  />
                  <Tooltip formatter={(value) => formatCurrency(value as number)} />
                  <Bar dataKey="amount" name="Valor" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Distribuição KYC por Nível
                </Typography>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('kyc')}>
                  Exportar
                </Button>
              </Box>
              <ResponsiveContainer width="100%" height={250} debounce={50}>
                <PieChart>
                  <Pie
                    data={kycLevelData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {kycLevelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Credit Details */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Status de Aplicações de Crédito
                </Typography>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('credit')}>
                  Exportar
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Percentual</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Chip label="Pendentes" color="warning" size="small" />
                      </TableCell>
                      <TableCell align="right">{mockAnalytics.credit.applications.pending}</TableCell>
                      <TableCell align="right">
                        {((mockAnalytics.credit.applications.pending /
                          (mockAnalytics.credit.applications.pending +
                           mockAnalytics.credit.applications.approved +
                           mockAnalytics.credit.applications.rejected)) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Chip label="Aprovadas" color="success" size="small" />
                      </TableCell>
                      <TableCell align="right">{mockAnalytics.credit.applications.approved}</TableCell>
                      <TableCell align="right">
                        {((mockAnalytics.credit.applications.approved /
                          (mockAnalytics.credit.applications.pending +
                           mockAnalytics.credit.applications.approved +
                           mockAnalytics.credit.applications.rejected)) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Chip label="Rejeitadas" color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">{mockAnalytics.credit.applications.rejected}</TableCell>
                      <TableCell align="right">
                        {((mockAnalytics.credit.applications.rejected /
                          (mockAnalytics.credit.applications.pending +
                           mockAnalytics.credit.applications.approved +
                           mockAnalytics.credit.applications.rejected)) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Taxa de Inadimplência
                </Typography>
                <Typography
                  variant="h6"
                  color={mockAnalytics.credit.defaultRate > 5 ? 'error.main' : 'success.main'}
                  fontWeight="bold"
                >
                  {mockAnalytics.credit.defaultRate}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Status de Transferências
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Completadas</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {mockAnalytics.transfers.byStatus.completed} ({((mockAnalytics.transfers.byStatus.completed / mockAnalytics.transfers.count) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(mockAnalytics.transfers.byStatus.completed / mockAnalytics.transfers.count) * 100}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Pendentes</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {mockAnalytics.transfers.byStatus.pending} ({((mockAnalytics.transfers.byStatus.pending / mockAnalytics.transfers.count) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(mockAnalytics.transfers.byStatus.pending / mockAnalytics.transfers.count) * 100}
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Falhadas</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {mockAnalytics.transfers.byStatus.failed} ({((mockAnalytics.transfers.byStatus.failed / mockAnalytics.transfers.count) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(mockAnalytics.transfers.byStatus.failed / mockAnalytics.transfers.count) * 100}
                  color="error"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Taxa de sucesso: <strong>{((mockAnalytics.transfers.byStatus.completed / mockAnalytics.transfers.count) * 100).toFixed(1)}%</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Stats */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Resumo Executivo
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {formatCurrency(
                    mockAnalytics.transfers.total +
                    mockAnalytics.billPayments.total +
                    mockAnalytics.credit.totalDisbursed
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Volume Total Processado
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {formatNumber(
                    mockAnalytics.transfers.count +
                    mockAnalytics.billPayments.count
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Transações Totais
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {mockAnalytics.credit.activeContracts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contratos de Crédito Ativos
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {mockAnalytics.kyc.approvalRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Taxa de Aprovação KYC
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Analytics;
